// purchaseOrderPaymentFromPo.js
//
// Create PURCHASE_ORDER_PAYMENT nodes and edge them to a Purchase Order.

import { createNode as createNodeFromFactory } from 'utilities/NodeFactory';
import { buildHierarchyEdgeOptions } from 'utilities/edgeHierarchy';
import { getResolvedFields } from 'utilities/NodeType';
import { upsertEdge } from 'services/nodes/NodeEdgeService';
import { setNodeDocs } from 'services/nodes/NodeWriteService';
import { readPurchaseOrderField } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';

export const PURCHASE_ORDER_PAYMENT_TYPE_KEY = 'PURCHASE_ORDER_PAYMENT';
export const PURCHASE_ORDER_TYPE_KEY = 'PURCHASE_ORDER';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTypeKey(value) {
  return normalizeString(value).toUpperCase();
}

/**
 * @param {unknown} typeKey
 * @returns {boolean}
 */
export function isPurchaseOrderPaymentTypeKey(typeKey) {
  return normalizeTypeKey(typeKey) === PURCHASE_ORDER_PAYMENT_TYPE_KEY;
}

/**
 * @param {object|null|undefined} paymentType
 * @param {string} fieldName
 * @returns {string}
 */
function resolvePaymentSectionKey(paymentType, fieldName) {
  const fields = Array.isArray(paymentType?.schema?.fields)
    ? paymentType.schema.fields
    : [];
  const resolved = getResolvedFields(PURCHASE_ORDER_PAYMENT_TYPE_KEY, { includeHidden: true }) || [];
  const all = [...fields, ...resolved];
  const fieldDef = all.find((field) => normalizeString(field?.name || field?.key) === fieldName);
  const source = normalizeString(
    fieldDef?.source || paymentType?.key || PURCHASE_ORDER_PAYMENT_TYPE_KEY,
  ).toLowerCase();
  if (paymentType?.dataSection && (!fieldDef?.source || fieldDef.source === paymentType.key)) {
    return paymentType.dataSection;
  }
  return source
    ? `data_${source}`
    : (paymentType?.dataSection || 'data_purchase_order_payment');
}

/**
 * Schedule a new payment on a purchase order.
 *
 * @param {object} options
 * @param {object} options.purchaseOrderNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.paymentType]
 * @param {object|null} [options.purchaseOrderType]
 * @param {number} [options.existingCount=0]
 * @param {number|null} [options.defaultAmountCents]
 * @returns {Promise<object>} Created payment node
 */
export async function createPurchaseOrderPayment({
  purchaseOrderNode,
  user,
  baseKey = null,
  paymentType = null,
  purchaseOrderType = null,
  existingCount = 0,
  defaultAmountCents = null,
}) {
  if (!purchaseOrderNode?.key || !user?.key) {
    throw new Error('Purchase order and user are required to schedule a payment.');
  }

  const resolvedBaseKey = baseKey || purchaseOrderNode.baseKey || null;
  const payment = await createNodeFromFactory(
    PURCHASE_ORDER_PAYMENT_TYPE_KEY,
    user,
    resolvedBaseKey || null,
  );

  const nextIndex = Math.max(0, Number(existingCount) || 0) + 1;
  payment.title = `Payment ${nextIndex}`;

  const sectionWrites = {};
  const writeField = (fieldName, value) => {
    if (value === undefined || value === null || value === '') return;
    const sectionKey = resolvePaymentSectionKey(paymentType, fieldName);
    if (!sectionWrites[sectionKey]) sectionWrites[sectionKey] = {};
    sectionWrites[sectionKey][fieldName] = value;
  };

  writeField('status', 'scheduled');

  if (defaultAmountCents != null && Number.isFinite(Number(defaultAmountCents))) {
    writeField('amount', Math.round(Number(defaultAmountCents)));
  }

  const terms = readPurchaseOrderField(purchaseOrderNode, 'paymentTerms', purchaseOrderType);
  if (terms != null && terms !== '') writeField('paymentTerms', terms);

  const dueDate = readPurchaseOrderField(purchaseOrderNode, 'dueDate', purchaseOrderType);
  if (dueDate != null && dueDate !== '') writeField('dueDate', dueDate);

  Object.entries(sectionWrites).forEach(([sectionKey, values]) => {
    payment[sectionKey] = {
      ...(payment[sectionKey] && typeof payment[sectionKey] === 'object' ? payment[sectionKey] : {}),
      ...values,
    };
  });

  await setNodeDocs([{ key: payment.key, payload: payment }], { merge: false });
  await upsertEdge(
    purchaseOrderNode.key,
    payment.key,
    buildHierarchyEdgeOptions({
      parentNode: purchaseOrderNode,
      childNode: payment,
      baseKey: resolvedBaseKey,
    }),
  );

  return payment;
}
